/**
 * The users and companies reads — the console's directory of who and what.
 *
 * Its own module for the same reason `queries-ops.ts` is: `queries.ts` is
 * capped at 500 lines, and this domain alone declares nineteen reads. Both are
 * re-exported from `@/lib/queries`, which stays the one import a screen or a
 * test reaches for.
 */
import { apiGet } from "@/lib/api";
import { AGGREGATE, DETAIL, LIVE_LIST } from "@/lib/query-freshness";
import type {
  CompaniesListResponse,
  CompanyAddressesResponse,
  CompanyDetailData,
  CompanyDocumentsResponse,
  CompanyFederalTaxResponse,
  CompanyMailroomResponse,
  CompanyMembersResponse,
  CompanyPaymentSummaryResponse,
  CompanyPaymentsResponse,
  CompanyRegisteredAgentResponse,
  CompanyShareholdersResponse,
  CompanyTaxFilingsResponse,
  CompanyWireTransferResponse,
  FaxRow,
  ListResponse,
  OrderRow,
  UserRow,
  UsersListResponse,
  CompanyStateRecordResponse,
  StateDiscrepanciesResponse,
} from "@/types";

/** Rows per page for the reads this module owns that page 25. */
const PAGE_SIZE = 25;
//
// This domain built its requests inside `useQuery` closures until now — 14 URLs
// that `api-requests.test.ts` could not execute, so a mistyped path passed every
// render test and answered NOT_IN_CATALOG in production. They are declared here
// like every other read.

/**
 * Users and companies have always paged 20 at a time; the wave-3+ screens page
 * 25. Kept as it was rather than converged, so the refactor changes no
 * behaviour an operator can see.
 */
const DIRECTORY_PAGE_SIZE = 20;

export interface UsersListParams {
  page: number;
  search: string;
  /** `all` | `onboarding` | `active` | `no_company` — the view tabs. */
  tab: string;
  role: string;
}

export function usersListQuery(params: UsersListParams) {
  return {
    ...LIVE_LIST,
    queryKey: [
      "users",
      params.page,
      params.search,
      params.tab,
      params.role,
    ] as const,
    queryFn: () =>
      apiGet<UsersListResponse>("/api/admin/users", {
        page: params.page,
        pageSize: DIRECTORY_PAGE_SIZE,
        tab: params.tab,
        search: params.search || undefined,
        role: params.role || undefined,
      }),
  };
}

export function userQuery(userId: string) {
  return {
    ...DETAIL,
    queryKey: ["user", userId] as const,
    queryFn: () =>
      apiGet<UserRow>(`/api/admin/users/${encodeURIComponent(userId)}`),
  };
}

/**
 * A user's orders. Upstream returns ONE flat user record — no orders, no
 * payments, no companies — so the old admin's Orders card rendered empty. The
 * orders collection already accepts `auth_user_id`, which is the whole fix.
 *
 * PAGED, because the section badge shows the whole total: a user with more than
 * `PAGE_SIZE` orders used to have the rest permanently unreachable while the
 * badge claimed they were there (A3-10).
 */
export function userOrdersQuery(authUserId: string, page: number) {
  return {
    ...LIVE_LIST,
    queryKey: ["user", authUserId, "orders", page] as const,
    queryFn: () =>
      apiGet<ListResponse<OrderRow>>("/api/admin/orders", {
        auth_user_id: authUserId,
        page,
        pageSize: PAGE_SIZE,
        sort_by: "created_at",
        sort_order: "desc",
      }),
  };
}

/**
 * What the companies screen asks for. Upstream still takes `formation_state`,
 * `status` and `onboarding_status` (the operator uses them); the console's
 * screen dropped those dropdowns (owner, 2026-09-25) and splits by type only.
 */
export interface CompaniesListParams {
  page: number;
  search: string;
  companyType: string;
}

export function companiesListQuery(params: CompaniesListParams) {
  return {
    ...LIVE_LIST,
    queryKey: [
      "companies",
      params.page,
      params.search,
      params.companyType,
    ] as const,
    queryFn: () =>
      apiGet<CompaniesListResponse>("/api/admin/companies", {
        page: params.page,
        pageSize: DIRECTORY_PAGE_SIZE,
        search: params.search || undefined,
        company_type: params.companyType || undefined,
      }),
  };
}

/**
 * The Cmd+K palette's lookups: a handful of hits per directory, matched on the
 * server (email, name, EIN or id), so the palette never filters them again.
 */
const PALETTE_HITS = 6;

export function paletteUsersQuery(search: string) {
  return {
    ...LIVE_LIST,
    queryKey: ["palette", "users", search] as const,
    queryFn: () =>
      apiGet<UsersListResponse>("/api/admin/users", {
        page: 1,
        pageSize: PALETTE_HITS,
        tab: "all",
        search,
      }),
  };
}

export function paletteCompaniesQuery(search: string) {
  return {
    ...LIVE_LIST,
    queryKey: ["palette", "companies", search] as const,
    queryFn: () =>
      apiGet<CompaniesListResponse>("/api/admin/companies", {
        page: 1,
        pageSize: PALETTE_HITS,
        search,
      }),
  };
}

/**
 * The company record itself. `include=entitlements,notes` is what makes the
 * Overview's feature list and notes arrive in the same round trip — the
 * standalone `/entitlements` and `/notes` routes carry nothing extra.
 */
export function companyQuery(companyId: string) {
  return {
    ...DETAIL,
    queryKey: ["company", companyId] as const,
    queryFn: () =>
      apiGet<CompanyDetailData>(
        `/api/admin/companies/${encodeURIComponent(companyId)}`,
        { include: "entitlements,notes" },
      ),
  };
}

export function companyAddressesQuery(companyId: string) {
  return {
    ...DETAIL,
    queryKey: ["company", companyId, "addresses"] as const,
    queryFn: () =>
      apiGet<CompanyAddressesResponse>(
        `/api/admin/companies/${encodeURIComponent(companyId)}/addresses`,
      ),
  };
}

export interface CompanyDocumentsParams {
  companyId: string;
  category: string;
  source: string;
  fileType: string;
}

export function companyDocumentsQuery(params: CompanyDocumentsParams) {
  return {
    ...DETAIL,
    queryKey: [
      "company",
      params.companyId,
      "documents",
      params.category,
      params.source,
      params.fileType,
    ] as const,
    queryFn: () =>
      apiGet<CompanyDocumentsResponse>(
        `/api/admin/companies/${encodeURIComponent(params.companyId)}/documents`,
        {
          category: params.category || undefined,
          source: params.source || undefined,
          file_type: params.fileType || undefined,
        },
      ),
  };
}

export function companyFederalTaxQuery(companyId: string) {
  return {
    ...DETAIL,
    queryKey: ["company", companyId, "federal-tax"] as const,
    queryFn: () =>
      apiGet<CompanyFederalTaxResponse>(
        `/api/admin/companies/${encodeURIComponent(companyId)}/services/federal-tax`,
      ),
  };
}

/**
 * What we hold about the company's Wyoming or Delaware record: the link, the
 * parsed facts, snapshots, discrepancies, candidates and archived PDFs. A pure
 * D1 read upstream (no registry call), so it is safe on the read-only path.
 */
export function companyStateRecordQuery(companyId: string) {
  return {
    ...DETAIL,
    queryKey: ["company", companyId, "state-record"] as const,
    queryFn: () =>
      apiGet<CompanyStateRecordResponse>(
        `/api/admin/companies/${encodeURIComponent(companyId)}/state-record`,
      ),
  };
}

export interface StateDiscrepanciesParams {
  page: number;
  status?: "open" | "resolved";
}

/** The work queue's "State records" list: discrepancies between us and the state. */
export function stateDiscrepanciesQuery(params: StateDiscrepanciesParams) {
  const status = params.status ?? "open";
  return {
    ...LIVE_LIST,
    queryKey: ["state-discrepancies", status, params.page] as const,
    queryFn: () =>
      apiGet<StateDiscrepanciesResponse>(
        "/api/admin/state-registry/discrepancies",
        { status, page: params.page, pageSize: PAGE_SIZE },
      ),
  };
}

/**
 * The unified federal + state filing timeline, with the EIN facts beside it.
 * It was catalogued and read by nothing; the Overview's filings summary is
 * what it exists for.
 */
export function companyTaxFilingsQuery(companyId: string) {
  return {
    ...DETAIL,
    queryKey: ["company", companyId, "tax-filings"] as const,
    queryFn: () =>
      apiGet<CompanyTaxFilingsResponse>(
        `/api/admin/companies/${encodeURIComponent(companyId)}/services/tax-filings`,
      ),
  };
}

export function companyMailroomQuery(companyId: string) {
  return {
    ...DETAIL,
    queryKey: ["company", companyId, "mailroom"] as const,
    queryFn: () =>
      apiGet<CompanyMailroomResponse>(
        `/api/admin/companies/${encodeURIComponent(companyId)}/services/mailroom`,
      ),
  };
}

export function companyRegisteredAgentQuery(companyId: string) {
  return {
    ...DETAIL,
    queryKey: ["company", companyId, "registered-agent"] as const,
    queryFn: () =>
      apiGet<CompanyRegisteredAgentResponse>(
        `/api/admin/companies/${encodeURIComponent(companyId)}/services/registered-agent`,
      ),
  };
}

/**
 * Every shareholder row in full: contact, address, roles and the tax id. The
 * same `shareholders` table backs `formation-shareholders`, which drops all of
 * that, so the formation and company details both read this one.
 */
export function shareholderRosterQuery(companyId: string) {
  return {
    ...DETAIL,
    queryKey: ["company", companyId, "shareholders"] as const,
    queryFn: () =>
      apiGet<CompanyShareholdersResponse>(
        `/api/admin/companies/${encodeURIComponent(companyId)}/shareholders`,
      ),
  };
}

/**
 * The LLC members read — richer rows than `formation-shareholders`, plus the
 * ownership total. Upstream is LLC-only and answers 400 for a C-Corp, so the
 * caller gates it on `company_type`.
 */
export function companyMembersQuery(companyId: string) {
  return {
    ...DETAIL,
    queryKey: ["company", companyId, "members"] as const,
    queryFn: () =>
      apiGet<CompanyMembersResponse>(
        `/api/admin/companies/${encodeURIComponent(companyId)}/members`,
      ),
  };
}

export interface CompanyPaymentsParams {
  companyId: string;
  page: number;
}

/**
 * The payment history, paged.
 *
 * `?format=detailed` — what this panel used to send — ignores every paging key
 * and returns the company's WHOLE history in one response, in a camelCase shape
 * (`serviceType`, `orderDate`, `paymentType`, `invoiceNumber`) that shares no
 * field name with the table's columns. The default format is the paged one, and
 * its rows are the snake_case records the table has always been written for.
 */
export function companyPaymentsQuery(params: CompanyPaymentsParams) {
  return {
    ...DETAIL,
    queryKey: ["company", params.companyId, "payments", params.page] as const,
    queryFn: () =>
      apiGet<CompanyPaymentsResponse>(
        `/api/admin/companies/${encodeURIComponent(params.companyId)}/payments`,
        {
          page: params.page,
          limit: PAGE_SIZE,
          sort_by: "created_at",
          sort_order: "desc",
        },
      ),
  };
}

/**
 * The whole-company money summary — total spent, this month, the change on last
 * month, the payment count — computed by ProductData over every row.
 *
 * The paged read's own `summary` covers only the rows on that page, and the
 * panel used to recompute the totals in the browser, which is domain logic the
 * guide puts upstream: mcp-admin asking the same question would get a different
 * answer. This is the read that has the answer.
 */
export function companyPaymentSummaryQuery(companyId: string) {
  return {
    ...AGGREGATE,
    queryKey: ["company", companyId, "payment-summary"] as const,
    queryFn: () =>
      apiGet<CompanyPaymentSummaryResponse>(
        `/api/admin/companies/${encodeURIComponent(companyId)}/payments`,
        { format: "detailed" },
      ),
  };
}

export function companyWireTransferQuery(companyId: string) {
  return {
    ...DETAIL,
    queryKey: ["company", companyId, "wire-transfer-subscription"] as const,
    queryFn: () =>
      apiGet<CompanyWireTransferResponse>(
        `/api/admin/companies/${encodeURIComponent(companyId)}/wire-transfer-subscription`,
      ),
  };
}

/**
 * A company's orders — `company_id` is a catalogued filter on the collection.
 *
 * PAGED for the same reason as `userOrdersQuery`: the section badge carries the
 * whole total, so a company past `PAGE_SIZE` orders had older ones unreachable
 * while the badge said otherwise (A3-10).
 */
export function companyOrdersQuery(companyId: string, page: number) {
  return {
    ...LIVE_LIST,
    queryKey: ["company", companyId, "orders", page] as const,
    queryFn: () =>
      apiGet<ListResponse<OrderRow>>("/api/admin/orders", {
        company_id: companyId,
        page,
        pageSize: PAGE_SIZE,
        sort_by: "created_at",
        sort_order: "desc",
      }),
  };
}

/** A company's fax history — same trick, and paged for the same reason (A3-10). */
export function companyFaxQuery(companyId: string, page: number) {
  return {
    ...LIVE_LIST,
    queryKey: ["company", companyId, "fax", page] as const,
    queryFn: () =>
      apiGet<ListResponse<FaxRow>>("/api/admin/fax", {
        company_id: companyId,
        page,
        pageSize: PAGE_SIZE,
      }),
  };
}
